type SortableListing = {
  id: number; createdAt: Date | string; isSponsered?: boolean | null;
  sponseredExpireDate?: Date | string | null;
  price?: number | null; year?: number | null; kilometers?: number | null; performance?: number | null;
};

/** Mirrors current ApplySorting, including PostgreSQL NULL ordering and ID ties.
 * Mixed-case server cases fall through after ToLowerInvariant; preserve that behavior here.
 * Sorting is a display projection: it never mutates the marker/result array.
 */
export function sortMapListings<T extends SortableListing>(items: readonly T[], sort: {active: string | null; direction: string | null}): T[] {
  if (!sort.active) return [...items]; // Keep the server's default order until explicitly changed.
  const key = sort.active.toLowerCase();
  const numeric = ['price', 'year', 'kilometers', 'performance'].includes(key);
  const direction = sort.direction === 'asc' ? 1 : -1;
  const now = Date.now();
  const sponsored = (item: T) => !!item.isSponsered && item.sponseredExpireDate != null && new Date(item.sponseredExpireDate).getTime() >= now;
  // API dates use UTC/unspecified UTC ISO strings. Keep sub-millisecond precision for ties.
  const dateKey = (value: Date | string) => {
    const text = value instanceof Date ? value.toISOString() : value;
    const match = /^(\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d)(?:\.(\d+))?(?:Z)?$/.exec(text);
    return match ? match[1] + '.' + (match[2] ?? '').padEnd(7, '0') : new Date(text).toISOString();
  };
  return [...items].sort((a,b) => {
    let order = 0;
    if (numeric) {
      const av = a[key as 'price'], bv = b[key as 'price'];
      order = av == null ? (bv == null ? 0 : 1) : bv == null ? -1 : av - bv;
    } else {
      const priority = Number(sponsored(b)) - Number(sponsored(a));
      if (priority) return priority; // Sponsorship stays first in both directions.
      const av = dateKey(a.createdAt), bv = dateKey(b.createdAt);
      order = av < bv ? -1 : av > bv ? 1 : 0;
    }
    return direction * (order || a.id - b.id);
  });
}
